/**
 * @fileoverview Componente de gerenciamento de roles.
 * Permite inclusão, alteração, exclusão e visualização de roles.
 * Interface responsiva com grid adaptável para desktop e mobile.
 * Integra com endpoints REST para CRUD de roles.
 * 
 * PROTEÇÃO DE ROLES CRÍTICAS:
 * - As roles 'admin' e 'user' são protegidas contra edição e exclusão
 * - Indicadores visuais mostram roles protegidas com chip "Protegida"
 * - Botões de edição/exclusão ficam desabilitados para roles protegidas
 * 
 * @module RoleManager
 * @requires react
 * @requires @mui/material
 * @requires ../../services/api
 * @since 1.0.0
 * @version 1.1.0 - Adicionada proteção para roles admin e user
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

/**
 * Componente principal de gerenciamento de roles.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
export default function RoleManager({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    alias: '',
    name: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [aliasFilter, setAliasFilter] = useState('');

  /**
   * Carrega lista de roles do backend com filtros.
   * Aplica filtros de busca por alias/nome.
   * @async
   * @function loadRoles
   * @returns {Promise<void>}
   */
  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/roles');
      const rolesData = response.data?.data?.roles || response.data?.roles || response.data || [];
      
      // Aplicar filtros localmente
      let filteredRoles = Array.isArray(rolesData) ? rolesData : [];
      
      if (searchTerm) {
        filteredRoles = filteredRoles.filter(role => 
          role.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setRoles(filteredRoles);
    } catch (error) {
      showSnackbar('Erro ao carregar roles', 'error');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Executa busca ao clicar no ícone de lupa.
   * @function handleSearch
   * @returns {void}
   */
  const handleSearch = useCallback(() => {
    setSearchTerm(searchInput);
  }, [searchInput]);

  useEffect(() => {
    if (open) {
      loadRoles();
    }
  }, [open, searchTerm, aliasFilter]);

  /**
   * Exibe mensagem de feedback via Snackbar.
   * @function showSnackbar
   * @param {string} message - Mensagem a ser exibida
   * @param {string} [severity='success'] - Tipo da mensagem
   * @returns {void}
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Abre formulário para criar novo role.
   * @function handleCreate
   * @returns {void}
   */
  const handleCreate = () => {
    setFormMode('create');
    setFormData({ alias: '', name: '' });
    setErrors({});
    setFormOpen(true);
  };

  /**
   * Abre formulário para editar role.
   * Bloqueia edição das roles protegidas 'admin' e 'user'.
   * @function handleEdit
   * @param {Object} role - Role a ser editado
   * @returns {void}
   */
  const handleEdit = (role) => {
    // Verificar se é uma role protegida
    if (role.alias === 'admin' || role.alias === 'user') {
      showSnackbar('Não é possível editar as roles "admin" e "user"', 'warning');
      return;
    }

    setFormMode('edit');
    setSelectedRole(role);
    setFormData({
      alias: role.alias || '',
      name: role.name || '',
    });
    setErrors({});
    setFormOpen(true);
  };

  /**
   * Abre modal de visualização de detalhes do role.
   * @function handleView
   * @param {Object} role - Role a ser visualizado
   * @returns {void}
   */
  const handleView = (role) => {
    setSelectedRole(role);
    setViewOpen(true);
  };

  /**
   * Remove role após confirmação.
   * Bloqueia exclusão das roles protegidas 'admin' e 'user'.
   * @async
   * @function handleDelete
   * @param {Object} role - Role a ser excluído
   * @returns {Promise<void>}
   */
  const handleDelete = async (role) => {
    // Verificar se é uma role protegida
    if (role.alias === 'admin' || role.alias === 'user') {
      showSnackbar('Não é possível excluir as roles "admin" e "user"', 'warning');
      return;
    }

    if (!window.confirm(`Deseja realmente excluir o role "${role.alias}"?`)) {
      return;
    }

    try {
      await api.delete(`/roles/${role.id}`);
      showSnackbar('Role excluído com sucesso');
      loadRoles();
    } catch (error) {
      showSnackbar('Erro ao excluir role', 'error');
    }
  };

  /**
   * Valida campos do formulário.
   * Verifica se alias é obrigatório.
   * @function validateForm
   * @returns {boolean} True se válido
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.alias.trim()) {
      newErrors.alias = 'Alias é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submete formulário (criar ou editar).
   * @async
   * @function handleSubmit
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        alias: formData.alias,
        name: formData.name
      };

      if (formMode === 'create') {
        await api.post('/roles', payload);
        showSnackbar('Role criado com sucesso');
      } else {
        await api.put(`/roles/${selectedRole.id}`, payload);
        showSnackbar('Role atualizado com sucesso');
      }
      
      setFormOpen(false);
      loadRoles();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erro ao salvar role', 'error');
    }
  };

  /**
   * Renderiza grid desktop com colunas alias e nome.
   * Desabilita ações para roles protegidas.
   * @function renderDesktopGrid
   * @returns {JSX.Element}
   */
  const renderDesktopGrid = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Alias</TableCell>
            <TableCell>Nome</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((role) => {
              const isProtected = role.alias === 'admin' || role.alias === 'user';
              return (
                <TableRow key={role.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.alias}
                      {isProtected && (
                        <Chip 
                          label="Protegida" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{role.name || '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={() => handleView(role)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isProtected ? "Role protegida" : "Editar"}>
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(role)}
                          disabled={isProtected}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isProtected ? "Role protegida" : "Excluir"}>
                      <span>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(role)}
                          disabled={isProtected}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * Renderiza grid mobile compacto.
   * Desabilita ações para roles protegidas.
   * @function renderMobileGrid
   * @returns {JSX.Element}
   */
  const renderMobileGrid = () => (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Alias</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((role) => {
                const isProtected = role.alias === 'admin' || role.alias === 'user';
                return (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {role.alias}
                          {isProtected && (
                            <Chip 
                              label="Protegida" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {role.name && <Box sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{role.name}</Box>}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleView(role)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(role)}
                        disabled={isProtected}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(role)}
                        disabled={isProtected}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Gerenciamento de Roles
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar por alias ou nome..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={handleSearch} title="Buscar">
                    <SearchIcon />
                  </IconButton>
                )
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Adicionar
            </Button>
          </Box>

          {isMobile ? renderMobileGrid() : renderDesktopGrid()}

          <TablePagination
            component="div"
            count={roles.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Formulário */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formMode === 'create' ? 'Novo Role' : 'Editar Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Alias *"
              value={formData.alias}
              onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
              error={!!errors.alias}
              helperText={errors.alias}
              fullWidth
            />
            <TextField
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {formMode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do Role</DialogTitle>
        <DialogContent>
          {selectedRole && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField label="Alias" value={selectedRole.alias} disabled fullWidth />
              <TextField 
                label="Nome" 
                value={selectedRole.name || '-'} 
                disabled 
                fullWidth 
              />
              {selectedRole.createdAt && (
                <TextField
                  label="Criado em"
                  value={new Date(selectedRole.createdAt).toLocaleString('pt-BR')}
                  disabled
                  fullWidth
                />
              )}
              {selectedRole.updatedAt && (
                <TextField
                  label="Atualizado em"
                  value={new Date(selectedRole.updatedAt).toLocaleString('pt-BR')}
                  disabled
                  fullWidth
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}